interface GitHubTreeItem {
  path: string;
  type: string;
  sha: string;
  url: string;
}

interface GitHubTreeResponse {
  tree: GitHubTreeItem[];
}

export interface MidiSearchResult {
  title: string;
  source: string;
  sourceId: string;
  fetchUrl: string;
}

export interface MidiSearchAdapter {
  id: string;
  name: string;
  search(query: string): Promise<MidiSearchResult[]>;
  fetch(result: MidiSearchResult): Promise<ArrayBuffer>;
}

async function loadGitHubMidis(
  owner: string,
  repo: string,
  cache: { items: GitHubTreeItem[] | null },
): Promise<GitHubTreeItem[]> {
  if (cache.items !== null) return cache.items;

  const url = `https://api.github.com/repos/${owner}/${repo}/git/trees/HEAD?recursive=1`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `[midi-search] GitHub trees API error for ${owner}/${repo}: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json() as unknown as GitHubTreeResponse;
  cache.items = data.tree.filter(item => item.type === 'blob' && item.path.endsWith('.mid'));
  return cache.items;
}

function encodeGitHubPath(path: string): string {
  return path.split('/').map(encodeURIComponent).join('/');
}

function rawUrl(owner: string, repo: string, path: string): string {
  return `https://raw.githubusercontent.com/${owner}/${repo}/HEAD/${encodeGitHubPath(path)}`;
}

function searchTree(
  tree: GitHubTreeItem[],
  query: string,
  owner: string,
  repo: string,
  source: string,
): MidiSearchResult[] {
  const q = query.toLowerCase();
  const results: MidiSearchResult[] = [];

  for (const item of tree) {
    if (!item.path.toLowerCase().includes(q)) continue;

    const lastSlash = item.path.lastIndexOf('/');
    const filename = lastSlash >= 0 ? item.path.slice(lastSlash + 1) : item.path;
    const title = filename.endsWith('.mid') ? filename.slice(0, -4) : filename;

    results.push({
      title,
      source,
      sourceId: item.path,
      fetchUrl: rawUrl(owner, repo, item.path),
    });
  }

  return results;
}

async function fetchArrayBuffer(url: string): Promise<ArrayBuffer> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`[midi-search] Fetch failed (${response.status}): ${url}`);
  }
  return response.arrayBuffer();
}

export class GitHubMidiAdapter implements MidiSearchAdapter {
  readonly id = 'github:thewildwestmidis';
  readonly name = 'Wild West MIDIs';
  private readonly _owner = 'thewildwestmidis';
  private readonly _repo = 'midis';
  private readonly _cache: { items: GitHubTreeItem[] | null } = { items: null };

  async search(query: string): Promise<MidiSearchResult[]> {
    const tree = await loadGitHubMidis(this._owner, this._repo, this._cache);
    return searchTree(tree, query, this._owner, this._repo, this.id);
  }

  async fetch(result: MidiSearchResult): Promise<ArrayBuffer> {
    return fetchArrayBuffer(result.fetchUrl);
  }
}

export class MutopiaMidiAdapter implements MidiSearchAdapter {
  readonly id = 'github:mutopia';
  readonly name = 'Mutopia Project';
  private readonly _owner = 'MutopiaProject';
  private readonly _repo = 'MutopiaProject';
  private readonly _cache: { items: GitHubTreeItem[] | null } = { items: null };

  async search(query: string): Promise<MidiSearchResult[]> {
    const tree = await loadGitHubMidis(this._owner, this._repo, this._cache);
    return searchTree(tree, query, this._owner, this._repo, this.id);
  }

  async fetch(result: MidiSearchResult): Promise<ArrayBuffer> {
    return fetchArrayBuffer(result.fetchUrl);
  }
}

export class MidishareMidiAdapter implements MidiSearchAdapter {
  readonly id = 'midishare';
  readonly name = 'Midishare';
  private _cache: { midis: { id: string; title?: string; name?: string }[] | null } = {
    midis: null,
  };
  async search(query: string): Promise<MidiSearchResult[]> {
    if (this._cache.midis === null) {
      try {
        const response = await fetch('https://midishare.dev/api/midis');
        if (!response.ok) {
          console.warn(
            `[midi-search] midishare.dev API error: ${response.status} ${response.statusText}`,
          );
          return [];
        }
        this._cache.midis = await response.json() as unknown as { id: string; title?: string; name?: string }[];
      } catch (err) {
        console.warn('[midi-search] midishare.dev fetch failed:', err);
        return [];
      }
    }

    const q = query.toLowerCase();
    const results: MidiSearchResult[] = [];

    for (const midi of this._cache.midis) {
      const displayName = midi.title ?? midi.name ?? midi.id;
      if (!displayName.toLowerCase().includes(q)) continue;

      results.push({
        title: displayName,
        source: this.id,
        sourceId: midi.id,
        fetchUrl: `https://midishare.dev/api/midi?id=${encodeURIComponent(midi.id)}`,
      });
    }

    return results;
  }
  async fetch(result: MidiSearchResult): Promise<ArrayBuffer> {
    try {
      const response = await fetch(result.fetchUrl);
      if (!response.ok) {
        throw new Error(
          `[midi-search] midishare.dev fetch failed: ${response.status} ${response.statusText}`,
        );
      }
      return response.arrayBuffer();
    } catch (err) {
      console.warn('[midi-search] midishare.dev fetch error:', err);
      throw err;
    }
  }
}

const adapters: MidiSearchAdapter[] = [
  new GitHubMidiAdapter(),
  new MutopiaMidiAdapter(),
  new MidishareMidiAdapter(),
];

export interface SearchResult {
  results: MidiSearchResult[];
  errors: string[];
}

export async function searchAllAdapters(query: string): Promise<SearchResult> {
  const errors: string[] = [];
  const settled = await Promise.allSettled(
    adapters.map(adapter =>
      adapter.search(query).catch((err: unknown): MidiSearchResult[] => {
        const msg = err instanceof Error ? err.message : String(err);
        const short = msg.includes('403') ? `${adapter.name}: rate limited (60/hr)` :
                      msg.includes('404') ? `${adapter.name}: not found` :
                      `${adapter.name}: ${msg.slice(0, 60)}`;
        errors.push(short);
        return [];
      }),
    ),
  );
  const results = settled.flatMap(r => r.status === 'fulfilled' ? r.value : []);
  return { results, errors };
}
